import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Button,
  Card,
  Chip,
  DataTable,
  PageHead,
  Pager,
  SearchInput,
  StateBlock,
  type Column,
} from '@w7/ui'
import { count, EMPTY } from '@/lib/format'
import type { MultipleDeviceGroup } from './multipleDeviceTypes'
import { useMultipleDevices } from './useMultipleDevices'

/**
 * Multiple Device Ids — Winner All Logs (TripleMaster only).
 *
 * Lists device ids shared by more than one account. Cheater flag is shown as a
 * chip tone; site colour coding from Winner is reduced to a muted site label.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

export function MultipleDeviceIdsPage() {
  const [page, setPage] = useState(1)
  const [deviceId, setDeviceId] = useState('')
  const [player, setPlayer] = useState('')
  const [applied, setApplied] = useState({ deviceId: '', player: '' })

  const { data, isLoading } = useMultipleDevices({
    page,
    pageSize: PAGE_SIZE,
    deviceId: applied.deviceId,
    player: applied.player,
  })

  const columns = useMemo<Column<MultipleDeviceGroup>[]>(
    () => [
      {
        id: 'deviceId',
        header: 'Device Id',
        cell: (row) => row.deviceId || EMPTY,
      },
      {
        id: 'playerCount',
        header: 'Duplicate Count',
        align: 'right',
        numeric: true,
        cell: (row) => row.playerCount,
      },
      {
        id: 'players',
        header: 'Players',
        cell: (row) => (
          <span className="w7-row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {row.players.map((p) => (
              <Chip
                key={`${row.id}-${p.username}`}
                size="sm"
                tone={p.isCheater ? 'loss' : p.accessLevel === 'Player' ? 'warn' : 'profit'}
              >
                {p.username}
                {p.siteUrl ? ` · ${p.siteUrl}` : ''}
              </Chip>
            ))}
          </span>
        ),
      },
    ],
    [],
  )

  const unavailable = data?.unavailable
  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1)

  const search = () => {
    setPage(1)
    setApplied({ deviceId: deviceId.trim(), player: player.trim() })
  }

  return (
    <>
      <PageHead
        title="Multiple Device Ids"
        subtitle="Accounts that share a device id — TripleMaster surveillance list from Winner All Logs"
      />

      {unavailable ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <StateBlock icon="info" title="No data found">
            {unavailable.reason}
            {unavailable.trackedBy ? ` Tracked by ${unavailable.trackedBy}.` : null}
          </StateBlock>
        </div>
      ) : null}

      <Card
        title="Filters"
        subtitle="Search by device id or player username"
        actions={
          <Button variant="primary" onClick={search} disabled={Boolean(unavailable)}>
            Search
          </Button>
        }
      >
        <div className="w7-row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px' }}>
            <SearchInput
              label="Device Id"
              value={deviceId}
              onChange={setDeviceId}
              placeholder="Search device id…"
            />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <SearchInput
              label="Player"
              value={player}
              onChange={setPlayer}
              placeholder="Search player…"
            />
          </div>
        </div>
      </Card>

      <Card
        flush
        title="Shared device ids"
        subtitle={
          isLoading
            ? 'Loading'
            : unavailable
              ? 'Source unavailable'
              : `${count(total)} matching groups`
        }
        footer={
          unavailable ? null : (
            <Pager
              page={data?.page ?? page}
              pageCount={pageCount}
              onChange={setPage}
              total={total}
              pageSize={PAGE_SIZE}
            />
          )
        }
      >
        <DataTable
          label="Multiple device ids"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={6}
          empty={
            unavailable ? (
              <StateBlock icon="users" title="Member-device list unavailable">
                Live sessions need Player-API admin member-device
                (`getPlayersWithSameDeviceIds`). Fixture rows appear only in demo mode.
              </StateBlock>
            ) : (
              <StateBlock icon="users" title="No shared device ids">
                No device groups match these filters for your seat.
              </StateBlock>
            )
          }
        />
      </Card>
    </>
  )
}
